import _ from 'lodash';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  KEY_RECOIL,
  TYPE_EVENT_OTHER,
  elementToCalculateStripManagementThumbnailInfo,
  setStripManagementThumbnailInfo,
  SIZE_STRIP_MANAGEMENT_THUMBNAIL,
} from '@/shared/config';
import {
  selectActiveTab,
  selectChangeStripData,
  selectEventStripsData,
  selectIsLoadingStrip,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { VALID_DEVICE_EVENT_STATUS_OPTIONS } from '../constants';
import { ID_DIV_STRIP_MANAGEMENT, checkStatusStripChange, getUpdateValue } from '../handler';

/**
 * Backs the action bar of the Strip Management tab with thumbnail sizing, checkbox state and the delete and reset actions. Recomputes strips per row and per column from the container size, merges pending changes over the selected strips to derive the checkbox and indeterminate flags, guards the last heart-rate strips from deletion, and binds the D key to delete.
 *
 * @returns An object carrying the thumbnail size, derived checkbox config, modal state and the action handlers.
 */
const useStripsActionButton = () => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const wrapperHeight = useRef<any>();

  const sizeThumbnailConfig = useReportStore((s) => s.sizeStripManagementThumbnailConfig);
  const setSizeThumbnailConfig = useReportStore(
    (s) => s.actions.setSizeStripManagementThumbnailConfig,
  );
  const selectedStrip: any = useReportStore(selectSelectedStrip(keyRecoil));
  const selectedMultipleStrip: any = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Writes the pending strip changes of the strip tab into the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new change list, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);
  const eventStripsData = useReportStore(selectEventStripsData(keyRecoil));
  const setReloadStripManagementAction = useReportStore((s) => s.actions.setReloadStripManagement);
  /**
   * Bumps the strip management reload counter in the report store. Forwards the value to the store action together with the TAB_3 key.
   *
   * @param v - The new counter value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setReloadStripManagement = (v: Parameters<typeof setReloadStripManagementAction>[1]) =>
    setReloadStripManagementAction(keyRecoil, v);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));

  const [size, setSize] = useState(sizeThumbnailConfig.name);
  const [isOpenPreventDeleteModal, setIsOpenPreventDeleteModal] = useState(false);

  /**
   * Recomputes how many strip thumbnails fit the Strip Management grid. Measures the container height, divides the available width and height by the thumbnail size plus gap to get strips per row and per column, stores that layout, and on the first pass nudges the reload counter so the grid re-renders.
   *
   * @param width - Thumbnail width in pixels.
   * @param height - Thumbnail height in pixels.
   * @param isFirstTime - Whether this is the initial measurement, which also triggers a grid reload.
   * @returns Nothing.
   */
  const calculateStripThumbnailInfo = ({ width, height, isFirstTime }: any) => {
    const stripWrapperEl = document.getElementById(ID_DIV_STRIP_MANAGEMENT);
    if (stripWrapperEl) {
      if (stripWrapperEl.offsetHeight) {
        wrapperHeight.current = stripWrapperEl.offsetHeight;
      }
      const { leftColumnWidth, paddingBothSide, stripBeatGap, paddingTopBottom } =
        elementToCalculateStripManagementThumbnailInfo;
      const stripPerRow = Math.floor(
        (window.innerWidth - leftColumnWidth - paddingBothSide + stripBeatGap) /
          (width + stripBeatGap),
      );
      const stripRow = Math.floor(
        (wrapperHeight.current - paddingTopBottom + stripBeatGap) / (height + stripBeatGap),
      );
      setStripManagementThumbnailInfo({ stripPerRow, stripRow });
      if (isFirstTime) {
        setReloadStripManagement((prev: any) => prev + _.round(Math.random() * 100));
      }
    }
  };

  /**
   * Switches the strip thumbnail size from the size dropdown. Stores the chosen size and, when it differs from the current one, recomputes the grid layout for the new dimensions before saving the size config.
   *
   * @param item - The clicked dropdown menu item whose value prop carries the size name.
   * @returns Nothing.
   */
  const onClickItemSize = ({ item }: any) => {
    setSize(item.props.value);
    const size = item.props.value;
    if (size !== sizeThumbnailConfig.name) {
      //* Change from current tab
      const newSizeThumbnail = (SIZE_STRIP_MANAGEMENT_THUMBNAIL as any)[size];
      calculateStripThumbnailInfo({
        width: newSizeThumbnail.width,
        height: newSizeThumbnail.height,
      });
      setSizeThumbnailConfig(newSizeThumbnail);
    }
  };

  /**
   * Applies an include-in-report or include-strip checkbox change to every selected strip. Merges pending changes over the selection, additionally pulls in strips sharing the same event id when the report flag is toggled, computes each new value, and stores the recomputed change list.
   *
   * @param name - The checkbox field being changed.
   * @param value - Its new checked value.
   * @returns Nothing.
   */
  const onChangeCheckbox = (name: any, value: any) => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    if (_.isEmpty(willUpdateList)) {
      return;
    }

    const mergeChanges = _.map(willUpdateList, (x) => {
      const change = _.find(changeStripData, (y) => y.id === x.id);
      if (change) {
        return { ...x, ...change };
      }
      return { ...x, ...(x.originalInfo || {}) };
    });
    if (name === 'isIncludedToReport') {
      const mergeDate = _.map(eventStripsData, (strip) => {
        const stripChange = _.find(changeStripData, (x) => x.id === strip.id);
        if (stripChange) {
          return { ...strip, ...stripChange };
        }
        return strip;
      });
      _.forEach(mergeDate, (x) => {
        const findSameEvent = _.find(willUpdateList, (y) => y.eventId === x.eventId);
        if (findSameEvent && !x.isRemoved) {
          mergeChanges.push(x);
        }
      });
    }
    const updatedList = _.map(_.uniqBy(mergeChanges, 'id'), (x) => ({
      ...x,
      ...getUpdateValue(name, value, x.isIncludedToReport),
    }));
    const changesData = checkStatusStripChange(updatedList, changeStripData);
    setChangeStripData(changesData);
  };

  /**
   * Applies one patch to every currently selected strip. Merges pending changes or originalInfo over each selected strip, spreads the patch on top, and stores the recomputed change list.
   *
   * @param updateData - The partial strip fields to apply to each selection.
   * @returns Nothing.
   */
  const updateFunc = (updateData: any) => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    const mergeChanges = _.map(willUpdateList, (x) => {
      const change = _.find(changeStripData, (y) => y.id === x.id);
      if (change) {
        return { ...x, ...change };
      }
      return { ...x, ...(x.originalInfo || {}) };
    });
    const updatedList = _.map(mergeChanges, (x) => ({ ...x, ...updateData }));
    const changesData = checkStatusStripChange(updatedList, changeStripData);
    setChangeStripData(changesData);
  };
  /**
   * Blocks deleting the last remaining maximum or minimum heart-rate strips. Collects the selected MAX_HR and MIN_HR ids, compares them against the total and the not-yet-removed ids of the merged strip data, and opens the prevent-delete modal when either group would be emptied.
   *
   * @returns True when the deletion was blocked and the modal opened.
   */
  const checkPreventDelete = () => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    const selectedHr: any = { MAX_HR: [], MIN_HR: [] };
    _.forEach(willUpdateList, (x) => {
      if (x.type === 'MAX_HR' || x.type === 'MIN_HR') {
        selectedHr[x.type].push(x.id);
      }
    });
    if (_.isEmpty(selectedHr.MAX_HR) && _.isEmpty(selectedHr.MIN_HR)) return false;
    const totalHr: any = { MAX_HR: [], MIN_HR: [] };
    const remainHr: any = { MAX_HR: [], MIN_HR: [] };
    _.forEach(eventStripsData, (strip) => {
      const stripChange = _.find(changeStripData, (x) => x.id === strip.id) || {};
      const updateData = { ...strip, ...stripChange };
      if (updateData.type === 'MAX_HR' || updateData.type === 'MIN_HR') {
        if (!updateData.isRemoved) {
          remainHr[updateData.type].push(updateData.id);
        }
        totalHr[updateData.type].push(updateData.id);
      }
    });
    if (
      selectedHr.MAX_HR.length === totalHr.MAX_HR.length ||
      selectedHr.MIN_HR.length === totalHr.MIN_HR.length
    ) {
      setIsOpenPreventDeleteModal(true);
      return true;
    }
    if (
      _.xor(selectedHr.MAX_HR, remainHr.MAX_HR).length === 0 ||
      _.xor(selectedHr.MIN_HR, remainHr.MIN_HR).length === 0
    ) {
      setIsOpenPreventDeleteModal(true);
      return true;
    }
    return false;
  };

  /**
   * Removes the selected strips from the report. Runs the heart-rate guard first and marks the strips as removed only when the guard does not block the deletion.
   *
   * @returns Nothing.
   */
  const onClickDelete = () => {
    const isPreventDelete = checkPreventDelete();
    if (!isPreventDelete) {
      updateFunc({ isRemoved: true });
    }
  };

  /**
   * Reverts the selected strips to their original values. Restores originalInfo with the removed flag cleared, also re-adds strips sharing the same event id whose title or include-in-report value differed, and stores the recomputed change list.
   *
   * @returns Nothing.
   */
  const onClickReset = () => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    const updatedList = _.map(willUpdateList, (x) => ({
      ...x,
      ...x.originalInfo,
      isRemoved: false,
    }));

    const mergeDate = _.map(eventStripsData, (strip) => {
      const stripChange = _.find(changeStripData, (x) => x.id === strip.id);
      if (stripChange) {
        return { ...strip, ...stripChange };
      }
      return strip;
    });
    _.forEach(mergeDate, (x) => {
      const findSameEvent = _.find(updatedList, (y) => y.eventId === x.eventId);
      if (findSameEvent) {
        const isChangedTitle =
          findSameEvent.type === TYPE_EVENT_OTHER && findSameEvent.title !== x.title;
        const isChangedInclude = findSameEvent.isIncludedToReport !== x.isIncludedToReport;
        const updateData = { ...x };
        if (isChangedInclude) {
          _.assign(updateData, {
            isIncludedToReport: findSameEvent.isIncludedToReport,
            isIncludedStrip: false,
          });
        }
        if (isChangedTitle) {
          _.assign(updateData, { title: findSameEvent.title });
        }
        if (isChangedInclude || isChangedTitle) {
          updatedList.push(updateData);
        }
      }
    });

    const changesData = checkStatusStripChange(updatedList, changeStripData);
    setChangeStripData(changesData);
  };

  useEffect(() => {
    const { width, height } = sizeThumbnailConfig;
    calculateStripThumbnailInfo({ width, height, isFirstTime: true });
  }, []);

  useLayoutEffect(() => {
    const { width, height } = sizeThumbnailConfig;
    calculateStripThumbnailInfo({ width, height });
  }, []);

  const configInfo: any = useMemo(() => {
    if (!_.isEmpty(selectedMultipleStrip)) {
      const mergeChanges = _.map(
        _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id'),
        (x) => {
          const change = _.find(changeStripData, (y) => y?.id === x?.id);
          if (change) return change;
          return { ...x, ...x.originalInfo };
        },
      );

      const total = mergeChanges.length;
      const countIncludedStrip = _.filter(mergeChanges, (x) => x.isIncludedStrip).length;
      const countIncludedToReport = _.filter(mergeChanges, (x) => x.isIncludedToReport).length;
      const data = {
        isIncludedStrip: countIncludedStrip === countIncludedToReport,
        isIncludedToReport: countIncludedToReport === total,
        isIndeterminateStrip: countIncludedStrip > 0 && countIncludedStrip < countIncludedToReport,
        isIndeterminateToReport: countIncludedToReport > 0 && countIncludedToReport < total,
        isDisabledStrip: countIncludedToReport === 0,
        foundEventChange: _.some(mergeChanges, (x) => x.updateStatus),
        isDisabled: _.every(mergeChanges, (x) => x.isRemoved),
        isValid: _.find(
          VALID_DEVICE_EVENT_STATUS_OPTIONS,
          (x) => x.value === !!_.some(mergeChanges, (x) => x.isValid),
        ),
      };

      return data;
    }

    if (!_.isEmpty(selectedStrip)) {
      const changeSelectedStrip = _.find(changeStripData, (x) => x.id === selectedStrip.id);
      if (changeSelectedStrip) {
        return {
          isIncludedStrip:
            changeSelectedStrip.isIncludedToReport && changeSelectedStrip.isIncludedStrip,
          isIncludedToReport: changeSelectedStrip.isIncludedToReport,
          isDisabledStrip: !changeSelectedStrip.isIncludedToReport,
          isDisabled: changeSelectedStrip.isRemoved,
          isValid: _.find(
            VALID_DEVICE_EVENT_STATUS_OPTIONS,
            (x) => x.value === changeSelectedStrip.isValid,
          ),
          foundEventChange: changeSelectedStrip.updateStatus,
        };
      }
      const { originalInfo } = selectedStrip;
      return {
        isIncludedStrip: originalInfo.isIncludedToReport && originalInfo.isIncludedStrip,
        isIncludedToReport: originalInfo.isIncludedToReport,
        isDisabledStrip: !originalInfo.isIncludedToReport,
        isValid: _.find(VALID_DEVICE_EVENT_STATUS_OPTIONS, (x) => x.value === originalInfo.isValid),
      };
    }
    return { isDisabled: true };
  }, [selectedMultipleStrip, selectedStrip, changeStripData]);

  useEffect(() => {
    /**
     * Binds the D key to deleting the selected strips while the strip tab is hovered. Ignores repeats, pointers outside the strip zone and modifier-key presses, and deletes only when the derived action config is not disabled.
     *
     * @param event - The window keydown event.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const tabZone = document.getElementById(ID_DIV_STRIP_MANAGEMENT);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }
      switch (keyCode) {
        case 68: {
          // Delete (D)
          if (!configInfo.isDisabled) {
            onClickDelete();
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMultipleStrip, selectedStrip, configInfo]);

  return {
    size,
    isLoadingStrip,
    configInfo,
    isOpenPreventDeleteModal,
    setIsOpenPreventDeleteModal,
    onClickItemSize,
    onChangeCheckbox,
    onClickDelete,
    onClickReset,
  };
};

export default useStripsActionButton;
